import React, { useEffect, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  List,
  ListItem,
  ListItemText,
  TextField,
} from '@mui/material';
import _ from 'lodash';
import superadminServices from 'app/services/superadmin-services';
import { SaveButton } from 'app/shared/ui';

const EMPTY_FORM = {
  company_id: '',
  store_id: '',
  device_type_id: '',
  dev_eui: '',
  name: '',
  lorawan_wireless_device_id: '',
};

const DEV_EUI_FORMAT = /^[0-9a-f]{16}$/i;

const describeError = (error) => {
  const errors = error?.response?.data?.errors;
  if (Array.isArray(errors) && errors.length) return errors.join(', ');
  // Sin detalle del backend, el código HTTP es lo único que orienta sobre qué falló.
  const status = error?.response?.status;
  return status ? `No se pudo registrar el equipo (error ${status}).` : 'No se pudo registrar el equipo.';
};

// Alta de un equipo LoRaWAN con todos sus puntos según su modelo (vault: 03 Producto/Modelo de equipos y puntos).
const RegisterPhysicalDeviceDialog = ({ open, onClose, companies = [], initialCompanyId = '', onRegistered }) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [options, setOptions] = useState({ stores: [], device_types: [] });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [registered, setRegistered] = useState(null);

  useEffect(() => {
    if (!open) return;
    setForm({ ...EMPTY_FORM, company_id: initialCompanyId || '' });
    setError(null);
    setRegistered(null);
  }, [open, initialCompanyId]);

  useEffect(() => {
    if (!open || !form.company_id) {
      setOptions({ stores: [], device_types: [] });
      return;
    }

    superadminServices.getDeviceEditOptions({ companyId: form.company_id })
      .then((resp) => setOptions({
        stores: resp?.stores || [],
        device_types: (resp?.device_types || []).filter((deviceType) => deviceType.interface === 'LoRa'),
      }))
      .catch(() => setOptions({ stores: [], device_types: [] }));
  }, [open, form.company_id]);

  const updateForm = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  const submit = async () => {
    if (!form.store_id || !form.device_type_id || !form.name.trim()) {
      setError('Completa local, modelo y nombre.');
      return;
    }
    if (!DEV_EUI_FORMAT.test(form.dev_eui.trim())) {
      setError('El DevEUI debe tener 16 caracteres hexadecimales.');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const created = await superadminServices.createPhysicalDevice({
        physical_device: {
          store_id: form.store_id,
          device_type_id: form.device_type_id,
          dev_eui: form.dev_eui.trim(),
          name: form.name.trim(),
          lorawan_wireless_device_id: form.lorawan_wireless_device_id.trim() || null,
        },
      });
      setRegistered(created);
      onRegistered?.(created);
    } catch (requestError) {
      setError(describeError(requestError));
    } finally {
      setSaving(false);
    }
  };

  const selectedCompany = _.find(companies, (company) => `${company.id}` === `${form.company_id}`) || null;
  const selectedStore = _.find(options.stores, (store) => `${store.id}` === `${form.store_id}`) || null;
  const selectedDeviceType = _.find(options.device_types, (deviceType) => `${deviceType.id}` === `${form.device_type_id}`) || null;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Registrar equipo LoRaWAN</DialogTitle>
      <DialogContent>
        {registered ? (
          <Box>
            <Alert severity="success" sx={{ mb: 1 }}>
              Equipo {registered.dev_eui} registrado con {registered.points.length}{' '}
              {registered.points.length === 1 ? 'punto' : 'puntos'}. Puedes renombrarlos y asignarles zona desde la lista de dispositivos.
            </Alert>
            {!!registered.previous_model && (
              <Alert severity="info" sx={{ mb: 1 }}>
                El equipo ya estaba registrado como {registered.previous_model}: sus puntos pasaron al modelo nuevo.
                {!!registered.removed_points?.length && ` Se quitó ${registered.removed_points.map((name) => `«${name}»`).join(', ')}, que este modelo no tiene.`}
              </Alert>
            )}
            <List dense>
              {registered.points.map((point) => (
                <ListItem key={point.id} disableGutters>
                  <ListItemText
                    primary={point.name}
                    secondary={`${point.channel ? `Canal ${point.channel}` : 'Equipo completo'} · código ${point.code}`}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        ) : (
          <Grid container spacing={2} sx={{ pt: 0.5 }}>
            <Grid item xs={12} md={6}>
              <Autocomplete
                options={companies}
                value={selectedCompany}
                getOptionLabel={(option) => option?.name || ''}
                onChange={(e, value) => updateForm({ company_id: value?.id || '', store_id: '' })}
                renderInput={(params) => <TextField {...params} required label="Empresa" />}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Autocomplete
                options={options.stores}
                value={selectedStore}
                getOptionLabel={(option) => option?.name || ''}
                onChange={(e, value) => updateForm({ store_id: value?.id || '' })}
                renderInput={(params) => <TextField {...params} required label="Local" />}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Autocomplete
                options={options.device_types}
                value={selectedDeviceType}
                getOptionLabel={(option) => _.compact([option?.brand, option?.model]).join(' ')}
                onChange={(e, value) => updateForm({ device_type_id: value?.id || '' })}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    required
                    label="Modelo"
                    helperText={selectedDeviceType?.description || 'Para corregir el modelo de un equipo ya registrado, regístralo de nuevo con su DevEUI.'}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="Nombre"
                helperText="En equipos con varios canales, cada punto se llama «Nombre · Circuito 1», etc."
                value={form.name}
                onChange={(e) => updateForm({ name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="DevEUI"
                helperText="16 caracteres hexadecimales, impreso en el equipo."
                value={form.dev_eui}
                onChange={(e) => updateForm({ dev_eui: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Id en AWS IoT (opcional)"
                helperText="Id del wireless device. Solo si el equipo va a recibir órdenes."
                value={form.lorawan_wireless_device_id}
                onChange={(e) => updateForm({ lorawan_wireless_device_id: e.target.value })}
              />
            </Grid>
          </Grid>
        )}
        {!!error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{registered ? 'Cerrar' : 'Cancelar'}</Button>
        {!registered && (
          <SaveButton loading={saving} disabled={saving} onClick={submit}>Registrar</SaveButton>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default RegisterPhysicalDeviceDialog;
